/**
 * Today's Tiffin of the Day at a store — `GET /api/tiffins/today` (Business
 * Rule #58): `{active, date, category, picks: [{…tiffin, upsell}]}`.
 *
 * One fetch per (store, IST day), shared by every mounted screen (Home bar,
 * product page, Cart, Checkout). The hook re-checks the IST date every minute
 * and when the app comes back to the foreground, so a screen left open past
 * midnight picks up the new day's tiffins. A failed fetch resolves to the
 * inactive shape — the feature simply doesn't show.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { todayIST } from '../utils/tiffinPlan';

const API = process.env.REACT_APP_BACKEND_URL;
const INACTIVE = Object.freeze({ active: false, date: null, category: null, picks: [] });
const cache = new Map(); // `${branchId}|${day}` → {data?, promise?}

/**
 * Fetch (once per store and IST day) today's tiffins at a store.
 * @param {string|null} branchId
 * @param {string} [day] - IST `YYYY-MM-DD`, defaults to today
 * @returns {Promise<{active: boolean, date: string|null, category: string|null, picks: Array}>}
 */
export function fetchTiffinToday(branchId, day = todayIST()) {
  if (!branchId) return Promise.resolve(INACTIVE);
  const key = `${branchId}|${day}`;
  const hit = cache.get(key);
  if (hit?.data) return Promise.resolve(hit.data);
  if (hit?.promise) return hit.promise;
  const promise = axios.get(`${API}/api/tiffins/today`, { params: { branch_id: branchId } })
    .then((res) => {
      const data = res?.data && typeof res.data === 'object' ? { ...INACTIVE, ...res.data } : INACTIVE;
      cache.set(key, { data });
      return data;
    })
    .catch(() => {
      cache.delete(key);
      return INACTIVE;
    });
  cache.set(key, { promise });
  return promise;
}

/** Forget cached days (tests, or after an admin saves the plan). */
export function resetTiffinCache() {
  cache.clear();
}

/**
 * Today's tiffins at the store, refreshed when the IST day rolls over.
 * @param {string|null} branchId
 * @returns {{today: {active: boolean, date: string|null, category: string|null, picks: Array}, loading: boolean}}
 */
export function useTiffinOfTheDay(branchId) {
  const [day, setDay] = useState(todayIST);
  const [state, setState] = useState(() => {
    const hit = branchId ? cache.get(`${branchId}|${todayIST()}`) : null;
    return { today: hit?.data || INACTIVE, loading: !!branchId && !hit?.data };
  });

  useEffect(() => {
    const tick = () => setDay(todayIST());
    const id = setInterval(tick, 60 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  useEffect(() => {
    let live = true;
    if (!branchId) { setState({ today: INACTIVE, loading: false }); return undefined; }
    fetchTiffinToday(branchId, day).then((data) => { if (live) setState({ today: data, loading: false }); });
    return () => { live = false; };
  }, [branchId, day]);

  return state;
}
